import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  FileText,
  Info,
  Paperclip,
  Sparkles,
  X,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatDuration, formatFileSize } from "@/lib/format";
import { type LibraryItem, expandSpintax, libraryFileUrl } from "@/lib/library";
import {
  type DialogSummary,
  type InboxMessage,
  MAX_ATTACHMENT_BYTES,
  useDialogMessages,
  useLiveDialogRefresh,
  useMarkDialogRead,
  useReplyToDialog,
  useSetReaction,
} from "@/lib/inbox";
import { Alert } from "@/components/ui/alert";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { peerDisplayName } from "./peer";
import { InboxMessageBubble } from "./InboxMessageBubble";
import { InboxAccountCard, LeadInfoPanel, LeadOrigin, LeadVia } from "./LeadInfo";
import { LeadStatusSelect } from "./LeadStatus";
import { ContentLibraryButton } from "./ContentLibrary";
import { KIND_ICONS } from "./libraryKinds";
import { EmojiPicker } from "./EmojiPicker";
import { SnippetPicker } from "./SnippetPicker";

/** What the next reply carries: a file from the computer, or one of the library's. */
type Attached = { file: File } | { material: LibraryItem };

export interface InboxConversationPaneProps {
  dialog: DialogSummary;
  /** Archive the dialog — or, for an archived one, bring it back. Parent owns
   *  the undo-toast + selection reset. */
  onArchive: () => void;
  /** Narrow screens show the list OR the conversation; this returns to the
   *  list. The button only renders below `lg`. */
  onBack?: () => void;
  /** Runs before a link leaves for another page — the drawer closes itself. */
  onNavigate?: () => void;
}

/**
 * The right-hand side of an inbox: the conversation and "about the lead".
 * Shared verbatim by InboxPage and InboxDrawer so behaviour (Cmd/Ctrl+Enter
 * send, auto-scroll, error surfacing) is identical in both. Uses `flex-1
 * min-h-0` so it fills its container without a `max-h-[70vh]` cap
 * (DESIGN-SYSTEM layout note, IA §4 Inbox).
 *
 * "About the lead" follows the pane's own width, not the screen's: from 50rem
 * on it is a column beside the conversation (the full inbox on a wide screen);
 * below that the header's button lays it over the conversation. The width is
 * asked through a container query — the `pane` container is this root.
 */
export function InboxConversationPane(props: InboxConversationPaneProps) {
  const { t } = useTranslation();
  // The account whose card is open over the inbox.
  const [accountCard, setAccountCard] = useState<string | null>(null);

  return (
    <div className="flex h-full min-h-0 flex-1 [container:pane/inline-size]">
      <Conversation {...props} onOpenAccount={setAccountCard} />
      <aside
        aria-label={t("inbox.about.title")}
        className="hidden w-[300px] shrink-0 overflow-y-auto border-l border-border [@container_pane_(min-width:50rem)]:block"
      >
        <LeadInfoPanel
          dialog={props.dialog}
          onOpenAccount={setAccountCard}
          onNavigate={props.onNavigate}
        />
      </aside>
      {accountCard && (
        <InboxAccountCard accountId={accountCard} onClose={() => setAccountCard(null)} />
      )}
    </div>
  );
}
InboxConversationPane.displayName = "InboxConversationPane";

/**
 * The conversation: peer header (status, archive), the message feed and the
 * reply composer.
 *
 * Note: reading messages here is passive — it must NOT bump last_used_at.
 * That's enforced server-side; this component only renders what the read
 * hooks return and never calls a write on mount.
 */
function Conversation({
  dialog,
  onArchive,
  onBack,
  onNavigate,
  onOpenAccount,
}: InboxConversationPaneProps & { onOpenAccount: (accountId: string) => void }) {
  const { t } = useTranslation();
  const msgs = useDialogMessages(dialog.id, { limit: 100 });
  const reply = useReplyToDialog();
  const react = useSetReaction();
  const markRead = useMarkDialogRead();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  // When the operator last replied in THIS dialog. From then on the dialog is
  // asked for on its own beat, so the answer does not wait for the account's
  // turn in the regular poll; what arrives while it is open counts as read.
  const [liveSince, setLiveSince] = useState<number | null>(null);
  // The message the next reply answers ("Reply" beside a bubble), if any.
  const [replyTo, setReplyTo] = useState<InboxMessage | null>(null);
  // The file the next reply carries (the paperclip, or the library), if any.
  const [attached, setAttached] = useState<Attached | null>(null);
  const file = attached && "file" in attached ? attached.file : null;
  const material = attached && "material" in attached ? attached.material : null;
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setLiveSince(null);
    setReplyTo(null);
    setAttached(null);
  }, [dialog.id]);
  // A picture is shown before it is sent; anything else by its name and size.
  const filePreview = useMemo(
    () => (file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null),
    [file],
  );
  useEffect(
    () => () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
    },
    [filePreview],
  );
  const megabytes = MAX_ATTACHMENT_BYTES / (1024 * 1024);
  const AttachedIcon = material ? KIND_ICONS[material.kind] : FileText;
  const attach = (picked: File | undefined) => {
    if (!picked) return;
    if (picked.size > MAX_ATTACHMENT_BYTES) {
      setError(t("inbox.attach.too_big", { mb: megabytes }));
      return;
    }
    setError(null);
    setAttached({ file: picked });
    textareaRef.current?.focus();
  };
  // A material of the library is taken into the reply — never sent by the
  // click. A text goes into the field, a file becomes the attachment (with its
  // own caption, if nothing is typed yet). Spintax resolves to one variant here,
  // so the operator sees exactly what will go out.
  const takeMaterial = (item: LibraryItem) => {
    if (item.kind === "text") {
      insertAtCursor(expandSpintax(item.text ?? ""));
      return;
    }
    setError(null);
    setAttached({ material: item });
    if (item.text && !text.trim()) setText(expandSpintax(item.text).slice(0, 4096));
    textareaRef.current?.focus();
  };
  useLiveDialogRefresh(dialog.id, liveSince, () => markRead.mutate(dialog.id));
  // "About the lead" laid over the conversation (where it is not a column).
  const [infoOpen, setInfoOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Insert at the caret (so emoji/snippets drop in where the operator is typing,
  // not always at the end). Falls back to append if the textarea isn't mounted.
  const insertAtCursor = (insert: string) => {
    const ta = textareaRef.current;
    const cur = text;
    const start = ta?.selectionStart ?? cur.length;
    const end = ta?.selectionEnd ?? cur.length;
    const next = (cur.slice(0, start) + insert + cur.slice(end)).slice(0, 4096);
    setText(next);
    const caret = Math.min(start + insert.length, 4096);
    requestAnimationFrame(() => {
      ta?.focus();
      ta?.setSelectionRange(caret, caret);
    });
  };

  // Auto-scroll to newest message when the feed grows.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [msgs.data?.items.length]);

  // API returns newest-first; flip to chronological for display.
  const ordered = useMemo(() => {
    if (!msgs.data) return [];
    return [...msgs.data.items].reverse();
  }, [msgs.data]);

  const name = peerDisplayName(dialog);
  const archiveLabel = t(dialog.archived ? "inbox.unarchive" : "inbox.archive");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = text.trim();
    if (!trimmed && !attached) return;
    try {
      await reply.mutateAsync({
        dialogId: dialog.id,
        text: trimmed,
        replyTo: replyTo?.tg_message_id,
        file: file ?? undefined,
        materialId: material?.id,
      });
      setText("");
      setReplyTo(null);
      setAttached(null);
      setLiveSince(Date.now());
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("inbox.reply_failed"));
    }
  };

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col">
      {/* Header. On a phone the actions take a row of their own: beside them
          the name was left some seventy pixels. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-3 [@container_pane_(min-width:24rem)]:flex-nowrap">
        <div className="flex min-w-0 basis-full items-center gap-2.5 [@container_pane_(min-width:24rem)]:basis-auto">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="-ml-1.5 shrink-0 rounded-md p-1.5 text-fg-muted hover:bg-bg-elevated/50 hover:text-fg-primary lg:hidden"
              aria-label={t("inbox.back_to_list")}
            >
              <ArrowLeft size={18} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
          <Avatar
            name={name}
            src={dialog.has_avatar ? `/api/inbox/dialogs/${dialog.id}/avatar` : null}
            colorKey={String(dialog.peer_tg_id)}
            size={36}
          />
          <div className="min-w-0">
            <p className="truncate font-medium text-fg-primary">{name}</p>
            <LeadVia dialog={dialog} onOpenAccount={onOpenAccount} />
            <LeadOrigin dialog={dialog} onNavigate={onNavigate} />
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <LeadStatusSelect dialog={dialog} />
          <button
            type="button"
            onClick={() => setInfoOpen((v) => !v)}
            aria-label={t("inbox.about.title")}
            aria-expanded={infoOpen}
            title={t("inbox.about.title")}
            className="rounded-md p-1.5 text-fg-muted hover:bg-bg-elevated/60 hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent [@container_pane_(min-width:50rem)]:hidden"
          >
            <Info size={16} strokeWidth={2} aria-hidden="true" />
          </button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onArchive}
            aria-label={archiveLabel}
            title={archiveLabel}
            iconLeft={
              dialog.archived ? (
                <ArchiveRestore size={15} strokeWidth={2} aria-hidden="true" />
              ) : (
                <Archive size={15} strokeWidth={2} aria-hidden="true" />
              )
            }
          >
            {/* In a narrow pane (the drawer, a phone) the icon alone. */}
            <span className="hidden [@container_pane_(min-width:30rem)]:inline">
              {archiveLabel}
            </span>
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {msgs.isLoading ? (
          <p className="text-sm text-fg-muted">{t("inbox.loading")}</p>
        ) : ordered.length === 0 ? (
          <p className="text-sm text-fg-muted">{t("inbox.no_messages")}</p>
        ) : (
          ordered.map((m) => (
            <InboxMessageBubble
              key={m.id}
              message={m}
              dialogId={dialog.id}
              peerName={name}
              onReply={() => {
                setReplyTo(m);
                textareaRef.current?.focus();
              }}
              onReact={(emoji) =>
                react.mutate({ dialogId: dialog.id, messageId: m.id, emoji })
              }
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <form
        onSubmit={submit}
        className="shrink-0 space-y-2 border-t border-border px-4 py-3"
      >
        {/* Known before the reply is typed, not after it has failed. */}
        {dialog.reply_warning && (
          <Alert
            tone={dialog.reply_warning.severity === "blocked" ? "danger" : "warning"}
            className="rounded-lg p-3 text-xs"
            action={
              <button
                type="button"
                onClick={() => onOpenAccount(dialog.account_id)}
                className="whitespace-nowrap rounded-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {t("inbox.open_account")}
              </button>
            }
          >
            {dialog.reply_warning.text}
          </Alert>
        )}
        {replyTo && (
          <div className="flex items-center gap-2 rounded-lg border-l-2 border-accent bg-bg-elevated/50 py-1.5 pl-3 pr-1.5 text-xs">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-accent">{t("inbox.reply.to")}</p>
              <p className="truncate text-fg-muted">
                {replyTo.text ?? (replyTo.has_media ? t("inbox.attached_media") : "—")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              aria-label={t("inbox.reply.cancel")}
              title={t("inbox.reply.cancel")}
              className="shrink-0 rounded-md p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        )}
        {attached && (
          <div className="flex items-center gap-2.5 rounded-lg border border-border bg-bg-elevated/50 py-1.5 pl-2 pr-1.5 text-xs">
            {filePreview || material?.kind === "photo" ? (
              <img
                src={filePreview ?? libraryFileUrl(material!.id)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-md object-cover"
              />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-bg-elevated text-fg-muted">
                <AttachedIcon size={18} strokeWidth={2} aria-hidden="true" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-fg-primary">
                {file ? file.name : (material!.file_name ?? material!.title)}
              </p>
              <p className="truncate text-fg-muted">
                {material && `${t("inbox.attach.from_library")} · `}
                {material?.kind === "voice" &&
                  `${t("inbox.media.kind.voice")} ${formatDuration(material.duration_seconds ?? 0)} · `}
                {formatFileSize(file ? file.size : material!.file_size)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAttached(null)}
              aria-label={t("inbox.attach.remove")}
              title={t("inbox.attach.remove")}
              className="shrink-0 rounded-md p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          maxLength={4096}
          placeholder={
            t(attached ? "inbox.attach.caption_placeholder" : "inbox.reply_placeholder") ?? ""
          }
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter sends; plain Enter inserts a newline so
            // multi-line replies stay easy to compose.
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              void submit(e as unknown as FormEvent);
            }
          }}
          className="w-full rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
        {error && <p className="break-words text-xs text-danger">{error}</p>}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <EmojiPicker onPick={insertAtCursor} />
            <SnippetPicker
              onPick={insertAtCursor}
              onPickMaterial={takeMaterial}
              currentText={text}
            />
            <ContentLibraryButton
              onPick={takeMaterial}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(e) => {
                attach(e.target.files?.[0]);
                // The same file can be picked again after it was removed.
                e.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label={t("inbox.attach.action")}
              title={t("inbox.attach.hint", { mb: megabytes })}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Paperclip size={18} strokeWidth={2} aria-hidden="true" />
            </button>
            {/* The place of the AI draft. There is no model behind it yet, and
                it says so: it cannot be pressed. */}
            <button
              type="button"
              aria-disabled="true"
              aria-label={t("inbox.ai_draft_soon")}
              title={t("inbox.ai_draft_soon")}
              className="inline-flex h-8 w-8 cursor-default items-center justify-center rounded-md text-fg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Sparkles size={18} strokeWidth={2} aria-hidden="true" />
            </button>
            <span className="ml-1 hidden text-xs text-fg-muted sm:inline">
              {t("inbox.send_shortcut")}
            </span>
          </div>
          <Button
            type="submit"
            size="sm"
            loading={reply.isPending}
            disabled={!text.trim() && !attached}
          >
            {t("inbox.send")}
          </Button>
        </div>
      </form>

      {/* "About the lead" over the conversation, where it is not a column. */}
      {infoOpen && (
        <div className="absolute inset-0 z-10 overflow-y-auto bg-bg-secondary [@container_pane_(min-width:50rem)]:hidden">
          <div className="flex items-center justify-between px-4 pt-3">
            <h2 className="text-sm font-semibold text-fg-primary">{t("inbox.about.title")}</h2>
            <button
              type="button"
              onClick={() => setInfoOpen(false)}
              aria-label={t("inbox.about.close")}
              className="rounded-md p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          <LeadInfoPanel dialog={dialog} onOpenAccount={onOpenAccount} onNavigate={onNavigate} />
        </div>
      )}
    </div>
  );
}
